// Step 24 — AI Deal Execution & Closing Readiness Workspace.
//
// Single export: AIDealExecution.
//
// This is a COMPOSITION layer over three existing endpoints:
//   - getDealSituationLive(workspaceId)   (Step 12)
//   - listDealActions(workspaceId)        (Step 13)
//   - listContracts(workspaceId)          (Step 16)
//
// Zero new backend endpoints. Zero new domain models. Zero new
// intelligence. Every value shown is authoritative data one of the
// three endpoints already returned.
//
// The workspace ONLY reads. There are no approve / reject / execute /
// complete / cancel / supersede / freeze / sign / send / close
// buttons. The existing human-controlled surfaces
// (/deals/:id/actions, /deals/:id/contracts/review, per-contract
// review page) remain the authoritative execution controls.
//
// Workspace isolation is server-side (each API re-validates via
// `for_user()`). The frontend never trusts a URL id.
//
// Degraded/partial data: if any single source fails, the counter for
// that source renders "—" (never 0). The other panels stay operational.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  getDealSituationLive,
  listContracts,
  listDealActions,
} from "../api";
import {
  AIEmpty,
  AIError,
  AIPageHeader,
  AISection,
} from "../shell/AIPrimitives";
import { useAIWorkspace } from "../shell/AIWorkspaceContext";
import AIAskQadaButton from "../shell/intelligence/AIAskQadaButton";
import AIExecutionAttentionChips from "../shell/execution/AIExecutionAttentionChips";
import AIExecutionBlocking from "../shell/execution/AIExecutionBlocking";
import AIExecutionConnections from "../shell/execution/AIExecutionConnections";
import AIExecutionFindings from "../shell/execution/AIExecutionFindings";
import AIExecutionReadiness from "../shell/execution/AIExecutionReadiness";
import AIExecutionTimeline from "../shell/execution/AIExecutionTimeline";
import {
  buildExecutionAskPrompt,
  buildExecutionInvestigationPrompt,
} from "../shell/execution/helpers";


function _errMsg(e) {
  if (!e) return null;
  return e?.body?.detail || e.message || "Kunde inte ladda data.";
}

export function AIDealExecution() {
  const { activeWorkspaceId, activeWorkspace } = useAIWorkspace();
  const [situation, setSituation] = useState(null);
  const [actions, setActions] = useState(null);
  const [contracts, setContracts] = useState(null);
  const [errors, setErrors] = useState({});

  const load = useCallback(
    async ({ signal } = {}) => {
      if (!activeWorkspaceId) return;

      // Parallel fetch — Promise.allSettled so one failure doesn't
      // hide the other two sources. A failed source renders "—" in
      // the readiness grid rather than 0.
      const [sRes, aRes, cRes] = await Promise.allSettled([
        getDealSituationLive(activeWorkspaceId, { signal }),
        listDealActions(activeWorkspaceId, { limit: 200 }, { signal }),
        listContracts(activeWorkspaceId, { limit: 200 }, { signal }),
      ]);

      const nextErrors = {};
      if (sRes.status === "fulfilled") {
        setSituation(sRes.value || null);
      } else if (sRes.reason?.name !== "AbortError") {
        nextErrors.situation = _errMsg(sRes.reason);
        setSituation(null);
      }
      if (aRes.status === "fulfilled") {
        setActions(aRes.value?.results || []);
      } else if (aRes.reason?.name !== "AbortError") {
        nextErrors.actions = _errMsg(aRes.reason);
        setActions(null);
      }
      if (cRes.status === "fulfilled") {
        setContracts(cRes.value?.results || []);
      } else if (cRes.reason?.name !== "AbortError") {
        nextErrors.contracts = _errMsg(cRes.reason);
        setContracts(null);
      }
      setErrors(nextErrors);
    },
    [activeWorkspaceId],
  );

  useEffect(() => {
    const c = new AbortController();
    load({ signal: c.signal });
    return () => c.abort();
  }, [load]);

  const combinedError = useMemo(() => {
    const parts = [];
    if (errors.situation) parts.push(`Deal situation: ${errors.situation}`);
    if (errors.actions) parts.push(`Åtgärder: ${errors.actions}`);
    if (errors.contracts) parts.push(`Avtal: ${errors.contracts}`);
    if (parts.length === 0) return null;
    return parts.join(" · ");
  }, [errors]);

  if (!activeWorkspaceId) {
    return <AIEmpty title="Ingen affär vald" />;
  }
  // No full-page spinner — each section already handles its own
  // loading/empty state, and readiness tiles render "—" while sources
  // are null (so the page is never blank).

  const askPrompt = buildExecutionAskPrompt({
    workspaceName: activeWorkspace?.organisation_name,
  });
  const investigatePrompt = buildExecutionInvestigationPrompt({
    workspaceName: activeWorkspace?.organisation_name,
  });

  const totalSources = 3;
  const okSources = [situation, actions, contracts].filter((x) => x != null).length;

  return (
    <>
      <AIPageHeader
        title="Genomförande"
        subtitle={activeWorkspace?.organisation_name}
        actions={
          <div style={{ display: "flex", gap: 8 }}>
            <AIAskQadaButton
              workspaceId={activeWorkspaceId}
              prompt={askPrompt}
              label="Fråga Qada om läget"
            />
            <AIAskQadaButton
              workspaceId={activeWorkspaceId}
              prompt={investigatePrompt}
              label="Djupare utredning"
            />
          </div>
        }
      />
      <AIError message={combinedError} />

      {/* Attention chips — deterministic observations only. */}
      <div className="qai-execution-attention-row">
        <AIExecutionAttentionChips
          actions={actions || []}
          contracts={contracts || []}
          situation={situation}
        />
      </div>

      {/* Degraded-source hint */}
      {okSources < totalSources && (
        <div className="qai-shell-empty-hint" style={{ marginBottom: 12 }}>
          Endast {okSources} av {totalSources} datakällor kunde laddas.
          Räknare för de källor som saknas visas som "—" — vyn drar
          inte slutsatser från saknad data.
        </div>
      )}

      <AIExecutionReadiness
        actions={actions || []}
        contracts={contracts || []}
        situation={situation}
        errors={errors}
      />

      <AIExecutionBlocking
        actions={actions || []}
        contracts={contracts || []}
        situation={situation}
        workspaceId={activeWorkspaceId}
      />

      <AIExecutionTimeline
        actions={actions || []}
        contracts={contracts || []}
        situation={situation}
      />

      <AIExecutionConnections
        actions={actions || []}
        contracts={contracts || []}
        situation={situation}
        workspaceId={activeWorkspaceId}
      />

      <AIExecutionFindings
        actions={actions || []}
        contracts={contracts || []}
        situation={situation}
        workspaceId={activeWorkspaceId}
      />

      <AISection title="Mänskligt kontrollerade granskningsvyer">
        <div className="qai-execution-review-links">
          <Link
            to={`/deals/${activeWorkspaceId}/actions`}
            className="qai-page-section-link"
          >
            Åtgärder — godkänn / starta / blockera / avsluta →
          </Link>
          <Link
            to={`/deals/${activeWorkspaceId}/contracts`}
            className="qai-page-section-link"
          >
            Avtal — jämförelse och intelligence →
          </Link>
          <Link
            to={`/deals/${activeWorkspaceId}/contracts/review`}
            className="qai-page-section-link"
          >
            Avtal — godkänn / skicka för signering →
          </Link>
          <Link
            to={`/deals/${activeWorkspaceId}/intelligence`}
            className="qai-page-section-link"
          >
            Intelligence-vy →
          </Link>
        </div>
        <div
          className="qai-shell-empty-hint"
          style={{ marginTop: 10 }}
        >
          Denna vy godkänner, avvisar, startar, avslutar, avbryter,
          ersätter, fryser, signerar eller stänger INGENTING. Alla
          livscykelbeslut sker manuellt via granskningsvyerna ovan.
        </div>
      </AISection>
    </>
  );
}

export default AIDealExecution;
